export const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "";

let loaderPromise = null;

/** Loads the Maps JavaScript API once and resolves with the Places library (Places API "New"). */
export function loadPlacesLibrary() {
  if (!GOOGLE_MAPS_API_KEY) return Promise.reject(new Error("REACT_APP_GOOGLE_MAPS_API_KEY is not set"));
  if (!loaderPromise) {
    loaderPromise = new Promise((resolve, reject) => {
      if (window.google?.maps?.importLibrary) {
        resolve();
        return;
      }
      const callbackName = "__cloudOfficeMapsReady";
      window[callbackName] = () => resolve();
      const script = document.createElement("script");
      script.src =
        `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(GOOGLE_MAPS_API_KEY)}` +
        `&loading=async&v=weekly&callback=${callbackName}`;
      script.async = true;
      script.onerror = () => {
        loaderPromise = null;
        reject(new Error("Could not load Google Maps"));
      };
      document.head.appendChild(script);
    }).then(() => window.google.maps.importLibrary("places"));
  }
  return loaderPromise;
}

function component(components, ...types) {
  for (const type of types) {
    const match = components.find((c) => (c.types || []).includes(type));
    if (match) return match.longText || match.long_name || "";
  }
  return "";
}

/** Address fields from a Places (New) Place with addressComponents, formattedAddress, displayName. */
export function parsePlace(place) {
  const components = place?.addressComponents || [];
  const street = [
    component(components, "subpremise"),
    component(components, "premise"),
    [component(components, "street_number"), component(components, "route")].filter(Boolean).join(" "),
  ]
    .filter(Boolean)
    .join(", ");
  const isBusiness = (place?.types || []).includes("establishment") || (place?.types || []).includes("point_of_interest");
  const name = place?.displayName || "";
  const line1 = [isBusiness && name && !street.includes(name) ? name : "", street].filter(Boolean).join(", ") || name;
  const line2 = [
    component(components, "neighborhood"),
    component(components, "sublocality_level_2"),
    component(components, "sublocality_level_1", "sublocality"),
  ]
    .filter(Boolean)
    .filter((part, idx, arr) => arr.indexOf(part) === idx)
    .join(", ");
  return {
    line1,
    line2,
    city: component(components, "locality", "postal_town", "administrative_area_level_3", "administrative_area_level_2"),
    state: component(components, "administrative_area_level_1"),
    country: component(components, "country"),
    zip: component(components, "postal_code"),
    formattedAddress: place?.formattedAddress || "",
    mapsUrl: place?.googleMapsURI || "",
  };
}

/** Google Maps link for an address (no API key needed). */
export function mapsSearchUrl(parts) {
  const query = (Array.isArray(parts) ? parts : [parts])
    .map((p) => String(p || "").trim())
    .filter(Boolean)
    .join(", ");
  return query ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}` : "";
}

/** Case/punctuation-insensitive match of a Google name against a list of names. */
export function matchByName(list, name, getName) {
  const norm = (v) =>
    String(v || "")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase()
      .replace(/[^a-z0-9]/g, "");
  const target = norm(name);
  if (!target) return null;
  return (
    list.find((item) => norm(getName(item)) === target) ||
    list.find((item) => {
      const n = norm(getName(item));
      return n && (n.startsWith(target) || target.startsWith(n));
    }) ||
    null
  );
}
